import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Numeric, ethers} from 'ethers';
import { JsonRpcProvider } from '@ethersproject/providers';
import { getReadonlyProvider } from '../core/providers';
import { firstValueFrom } from 'rxjs';
import { from, Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
const ETH_SENTINEL = '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee';
const NATIVE_ETH_MATCHES = [
  '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee',
  '0x0000000000000000000000000000000000000000'
];

const abi = [
  'function balanceOf(address) view returns (uint256)',
  'function decimals() view returns (uint8)'
];

@Injectable({
  providedIn: 'root'
})
export class SwapApiService {
  constructor(private http: HttpClient) {}
  private provider!: ethers.BrowserProvider;

  private readonlyChainId = 1;

  /** appelé par le composant quand le réseau change */
  setChainId(id: number | null) {
    this.readonlyChainId = id ?? 1;        // fallback L1
  }

  getAvailableTokens(): Observable<any[]> {
    return this.http.get<any[]>(
      `${environment.backendUrl}/tokens/available`,
      {
        headers: new HttpHeaders({
          'ngrok-skip-browser-warning': '1'
        })
      }
    );
  }

  //getAvailableTokens(): Observable<any[]> {
  //  return this.http.get<any[]>(
  //    `${environment.backendUrl}/tokens/smart-live`,
  //    {
  //      headers: new HttpHeaders({
  //        'ngrok-skip-browser-warning': '1'
  //      })
  //    }
  //  );
  //}
  getGasTrends(chainId: number): Observable<Record<'1h' | '6h' | '24h', number>> {
    const headers = new HttpHeaders({ 'ngrok-skip-browser-warning': '1' });

    return this.http.get<Record<'1h' | '6h' | '24h', number>>(
      `${environment.backendUrl}/gas-trend?chainId=${chainId}`,
      { headers }
    );
  }

 getSwapGasTrendDune(
  tokenIn: string,
  tokenOut: string,
  chainId: number,
   period: string,
  amountIn: number
): Observable<{ trend: number }> {
  const headers = new HttpHeaders({ 'ngrok-skip-browser-warning': '1' });

  const chainName = this.getChainName(chainId);
  const params = new HttpParams()
    .set('tokenIn', tokenIn)
    .set('tokenOut', tokenOut)
    .set('blockchain', chainName)
    .set('period', period)
    .set('amountIn', amountIn);

  const url = `${environment.backendUrl}/swap/swap-gas-trend-dune`;

  console.log('[SmartSwap] 🧪 Calling Dune Gas Trend API with:');
  console.log('→ amountIn:', amountIn);
  console.log('→ tokenIn:', tokenIn);
  console.log('→ tokenOut:', tokenOut);
  console.log('→ chainId:', chainId, '→', chainName);
  console.log('→ period:', period);
  console.log('→ URL:', url);
  console.log('→ Full params:', params.toString());

  return this.http.get<{ trend: number }>(url, { params, headers });
}



 getMarketTrends(tokenIn: string, tokenOut: string, chainId: number, amountIn: number): Observable<any[]> {
  const headers = new HttpHeaders({ 'ngrok-skip-browser-warning': '1' });

  const params = new HttpParams()
    .set('sellToken', tokenIn)
    .set('buyToken', tokenOut)
    .set('blockchain', this.getChainName(chainId)) // arbitrum, ethereum...
    .set('amountIn', amountIn.toString());

  return this.http.get<any[]>(`${environment.backendUrl}/swap/market-trend-dune`, { params, headers });
}
private getChainName(chainId: number): string {
  switch (chainId) {
    case 1: return 'ethereum';
    case 42161: return 'arbitrum';
    case 137: return 'polygon';
    default: return 'unknown';
  }
}

  getGasPriceTrendDune(chainId: number): Observable<Record<'1h' | '3h' | '6h' | '24h', number>> {
    const headers = new HttpHeaders({ 'ngrok-skip-browser-warning': '1' });
    const chainName = this.getChainName(chainId);

    const params = new HttpParams().set('blockchain', chainName);

    const url = `${environment.backendUrl}/swap/gas-price-trend-dune`;

    console.log('[SwapApiService] 📡 Appel à Dune GasPriceTrend:', url, params.toString());

    return this.http.get<{ success: boolean; trend: Record<string, number> }>(url, { headers, params }).pipe(
      switchMap(res => {
        if (!res.success) {
          throw new Error('Erreur backend Dune');
        }
        return of(res.trend as Record<'1h' | '3h' | '6h' | '24h', number>);
      })
    );
  }


  getRecommendedGasPrice(chainId: number = this.readonlyChainId): Promise<string> {
    const url = `${environment.backendUrl}/gas/price`;

    const params = new HttpParams()
      .set('chainId', chainId.toString());

    const headers = new HttpHeaders({
      'ngrok-skip-browser-warning': '1'        // ← seulement si tu utilises ngrok
    });

    // Le backend renvoie : { gasPriceWei: "535177608" }
    return firstValueFrom(
      this.http.get<{ gasPriceWei: string }>(url, { params, headers })
    ).then(r => r.gasPriceWei);
  }

  getQuote(
    sellToken: string,
    buyToken: string,
    sellAmount: string,
    taker: string,
    chainId: number = this.readonlyChainId       // param facultatif si besoin
  ): Observable<any> {

    /* 1️⃣ On commence par récupérer le gas price dynamique */
    return from(this.getRecommendedGasPrice(chainId)).pipe(
      switchMap(gasPriceWei => {

        /* 2️⃣ Query-params complets avec le gas price */
        let params = new HttpParams()
          .set('sellToken', sellToken)
          .set('buyToken', buyToken)
          .set('sellAmount', sellAmount)
          .set('chainId', chainId)
          .set('taker', taker)
          .set('gasPrice', gasPriceWei)      // ← injection dynamique
          .set('slippage', '1');             // (ou récupéré depuis tes settings)

        const headers = new HttpHeaders({
          'ngrok-skip-browser-warning': '1'
        });

        const url = `${environment.backendUrl}/swap/quote`;
        console.log('➡️ Requête vers API :', url, params.toString());

        /* 3️⃣ Appel GET (Observable retourné par switchMap) */
        return this.http.get<any>(url, { params, headers });
      })
    );
  }




  setProvider(raw: any) {           // raw = provider EIP-1193
    this.provider = new ethers.BrowserProvider(raw);
  }



  getGaslessQuote(
    sellToken: string,
    buyToken: string,
    sellAmount: string,
    slippage: string = '0.5',
    chainId: string = '1'
  ): Observable<any> {
    // Remplace ETH par WETH si besoin (uniquement pour inToken)
    const WETH_BY_CHAIN: { [key: string]: string } = {
      '1': '0xC02aaa39b223FE8D0A0e5C4F27eAD9083C756Cc2',     // Ethereum Mainnet
      '42161': '0x82af49447d8a07e3bd95bd0d56f35241523fbab1', // Arbitrum
      '10': '0x4200000000000000000000000000000000000006',   // Optimism
      '137': '0x7ceB23fD6bC0adD59E62ac25578270cFf1b9f619',   // Polygon
      // Ajoute d'autres chaines ici si besoin
    };

    const adjustedSellToken = sellToken.toLowerCase() === '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee'
      ? WETH_BY_CHAIN[chainId] || sellToken
      : sellToken;

    return this.http.get<any>(`${environment.backendUrl}/gas/price`, {
      params: { chainId: chainId },
      headers: new HttpHeaders({ 'ngrok-skip-browser-warning': '1' })
    }).pipe(
      switchMap(res => {
        const gasPriceWei = BigInt(res.gasPriceWei);
        const gasPriceDecimals = gasPriceWei.toString();

        const params = new HttpParams()
          .set('inTokenAddress', adjustedSellToken)
          .set('outTokenAddress', buyToken)
          .set('amountDecimals', sellAmount)
          .set('slippage', slippage)
          .set('gasPriceDecimals', gasPriceDecimals);

        const baseUrl = `https://open-api.openocean.finance/v4/gasless/${chainId}/quote`;
        const fullUrl = `${baseUrl}?${params.toString()}`;
        console.log('[DEBUG] OpenOcean Gasless URL:', fullUrl);

        return this.http.get<any>(baseUrl, { params });
      })
    );
  }








  // SwapApiService.ts
  getGaslessSwap(body: any): Observable<any> {
    const bodySanitized = JSON.parse(JSON.stringify(body, (_, v) =>
      typeof v === 'bigint' ? v.toString() : v
    ));

    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.post(
      'https://open-api.openocean.finance/v4/gasless/1/swap',
      bodySanitized,
      { headers }
    );
  }








  getQuoteForAlert(
    sellToken: string,
    buyToken: string,
    sellAmount: string,
    taker: string,
    chainId: number          // ← remplace Numeric par number
  ): Observable<any> {

    /* cast → string pour être 100 % type-safe */
    const params = new HttpParams()
      .set('sellToken', sellToken)
      .set('buyToken', buyToken)
      .set('sellAmount', sellAmount)
      .set('chainId', chainId.toString())   // 👈
      .set('taker', taker);

    const headers = new HttpHeaders({ 'ngrok-skip-browser-warning': '1' });

    const url = `${environment.backendUrl}/swap/quoteAlert`;
    console.log('➡️ Requête vers API :', url, params.toString());

    return this.http.get<any>(url, { params, headers });
  }




  

getProvider(chainId: number): JsonRpcProvider {
  return new JsonRpcProvider(this.getRpcUrl(chainId));
}


private getRpcUrl(chainId: number): string {
  switch (chainId) {
    case 1: return 'https://eth-mainnet.g.alchemy.com/v2/gCvph7eowzBP0e2uUN177AELQnht-CXN';
    case 137: return 'https://polygon-mainnet.g.alchemy.com/v2/gCvph7eowzBP0e2uUN177AELQnht-CXN';
    case 56: return 'https://bnb-mainnet.g.alchemy.com/v2/gCvph7eowzBP0e2uUN177AELQnht-CXN';
    case 10: return 'https://rpc.ankr.com/optimism/3daf9fb1a12a913bd97e56c90e6d15db5dd60f66b4d67b45736ba3f8f3e604f2';
    case 42161: return 'https://arb-mainnet.g.alchemy.com/v2/id2ETOtmTy1BpEYYsngoBNiBwnx2bGeD';
    default: throw new Error(`RPC non défini pour chainId=${chainId}`);
  }
}






async getBalance(token: any, addr: string, chainId: number): Promise<string> {
  const provider = getReadonlyProvider(chainId); // ✅ bon réseau

  if (NATIVE_ETH_MATCHES.includes(token.address.toLowerCase())) {
    const balance = await provider.getBalance(addr);
    return ethers.formatEther(balance);
  }

  const erc20 = new ethers.Contract(token.address, abi, provider);
  const [rawBalance, decimals] = await Promise.all([
    erc20["balanceOf"](addr),
    erc20["decimals"]()
  ]);

  return ethers.formatUnits(rawBalance, decimals);
}




}
